import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { supabase } from '../services/supabaseClient';
import { Loader2, ShieldAlert } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactElement;
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [profile, setProfile] = useState<any>(null);

  useEffect(() => {
    // 1. Obter sessão atual
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setUser(session.user);
        fetchProfile(session.user.id);
      } else {
        setLoading(false);
      }
    });

    // 2. Escutar mudanças no estado de autenticação
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        setUser(session.user);
        fetchProfile(session.user.id);
      } else {
        setUser(null);
        setProfile(null);
        setLoading(false);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const fetchProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Erro ao buscar perfil:', error);
      } else {
        setProfile(data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] bg-background">
        <Loader2 className="animate-spin text-primary-500 mb-4" size={48} />
        <p className="text-neutral-500 text-lg font-medium">Carregando informações de acesso...</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/cowork/login" replace />;
  }

  if (profile && !profile.approved) {
    return (
      <div className="max-w-md mx-auto my-16 px-6 py-12 bg-white rounded-3xl shadow-xl flex flex-col items-center text-center border border-primary-100">
        <div className="bg-primary-100 p-4 rounded-full text-primary-500 mb-6">
          <ShieldAlert size={40} />
        </div>
        <h2 className="text-3xl font-extrabold text-primary-900 mb-4">Acesso Pendente</h2>
        <p className="text-neutral-600 mb-6 leading-relaxed">
          Sua conta (<strong>{user.email}</strong>) foi criada com sucesso! No entanto, o acesso à agenda do Coworking exige aprovação do administrador.
        </p>
        <div className="bg-primary-50 p-4 rounded-xl text-sm text-primary-700 font-medium mb-6 w-full">
          Por favor, solicite a liberação da sua conta ao administrador da Clínica Psi Mazulo.
        </div>
        <button
          onClick={() => supabase.auth.signOut()}
          className="text-primary-700 font-semibold hover:text-primary-900 transition-colors"
        >
          Sair da Conta
        </button>
      </div>
    );
  }

  return children;
}
